import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMedicalInfo } from '../services/assuranceService';

export default function MedicalDeclaration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [hasSpouse, setHasSpouse] = useState(false);
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [declared, setDeclared] = useState(false);
  const [medicalQuestions, setMedicalQuestions] = useState([]);

  useEffect(() => {
    // Récupérer les données du formulaire médical
    const savedFormData = localStorage.getItem('step6FormData');
    const savedHasSpouse = localStorage.getItem('hasSpouse');
    const savedNumberOfChildren = localStorage.getItem('numberOfChildren');

    if (savedFormData) setFormData(JSON.parse(savedFormData));
    if (savedHasSpouse) setHasSpouse(savedHasSpouse === 'true');
    if (savedNumberOfChildren) setNumberOfChildren(parseInt(savedNumberOfChildren));

    // Charger les questions médicales depuis l'API
    const fetchMedicalQuestions = async () => {
      try {
        const result = await getMedicalInfo();
        if (result && result.data) {
          setMedicalQuestions(result.data);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des questions médicales:', err);
      }
    };

    fetchMedicalQuestions();
  }, []);

  const handleContinue = () => {
    if (!declared) {
      alert('Veuillez confirmer la déclaration avant de continuer.');
      return;
    }
    navigate('/congratulation');
  };

  // Fonction pour extraire les réponses médicales du formulaire
  const getMedicalResponses = (prefix) => {
    const responses = [];
    if (!formData || medicalQuestions.length === 0) return responses;

    // Créer une map des questions par clé pour un accès rapide
    const questionMap = {};
    medicalQuestions.forEach(q => {
      questionMap[q.key] = q;
    });

    // Récupérer les questions depuis le localStorage ou afficher toutes les clés du formulaire
    Object.keys(formData).forEach(key => {
      if (key.startsWith(`${prefix}[`)) {
        // Extraire le nom de la question et la réponse
        const questionMatch = key.match(/\[(.*?)\]/);
        if (questionMatch) {
          const questionKey = questionMatch[1];
          const value = formData[key];
          
          // Ignorer les champs d'info supplémentaires (traités séparément)
          if (value && !key.includes('_infos')) {
            const question = questionMap[questionKey];
            
            if (question) {
              // Convertir la réponse en français si c'est un booléen
              let displayValue = value;
              if (question.type === 'boolean') {
                if (Array.isArray(value)) {
                  displayValue = value.map(v => v === 'true' ? 'Oui' : v === 'false' ? 'Non' : v).join(', ');
                } else {
                  displayValue = value === 'true' ? 'Oui' : value === 'false' ? 'Non' : value;
                }
              } else if (Array.isArray(value)) {
                displayValue = value.join(', ');
              }
              
              // Si la réponse est "Oui" et qu'il y a des champs additionnels (more_infos)
              if ((displayValue === 'Oui' || value === 'true') && question.more_infos) {
                const infoKey = `${prefix}[${questionKey}_infos]`;
                const infoValue = formData[infoKey];
                if (infoValue) {
                  const fieldLabel = question.more_infos.field?.label || question.more_infos.field?.name || 'Info';
                  displayValue += `\n${fieldLabel}: ${infoValue}`;
                }
              }
              
              responses.push({ 
                question: question.name, 
                answer: displayValue
              });
            }
          }
        }
      }
    });

    return responses;
  };

  const principalResponses = getMedicalResponses('principal_insured');
  const spouseResponses = hasSpouse ? getMedicalResponses('spouse') : [];
  const childrenResponses = numberOfChildren > 0 ? Array.from({ length: numberOfChildren }, (_, i) => ({
    childNumber: i + 1,
    responses: getMedicalResponses(`child_${i + 1}`)
  })) : [];

  return (
    <div className="py-10 lg:py-16 bg-[#e4eef9]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-center text-4xl font-bold">Formulaire de souscription</h1>
        
        <div className="mt-8 bg-white p-6 lg:p-10 rounded-xl shadow-sm">
          {/* Section Déclaration */}
          <div className="mb-8 p-6 bg-blue-50 rounded-lg border-l-4 border-primary">
            <h2 className="text-lg font-bold text-primary mb-4">Déclaration sur l'honneur</h2>
            <div className="text-sm text-gray-700 space-y-3 mb-6 leading-relaxed">
              <p>
                <strong>Je soussigné(e)</strong> certifie sincères et véritables mes déclarations aux questions du présent bulletin d'adhésion, et accepte de me soumettre sur demande de la Société d'Assurances à une visite médicale de son médecin conseil.
              </p>
              <p>
                Toute fausse réponse ou omission au présent questionnaire est soumise aux sanctions prévues par les articles 18 et 19 du code CIMA.
              </p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer p-4 bg-white rounded-lg border-2 border-gray-300 hover:border-primary transition">
              <input
                type="checkbox"
                checked={declared}
                onChange={(e) => setDeclared(e.target.checked)}
                className="h-5 w-5 text-primary cursor-pointer"
              />
              <span className="text-gray-700 font-medium">
                Je confirme les déclarations ci-dessus et accepte les conditions
              </span>
            </label>
          </div>

          {/* Section Récapitulatif Questionnaire Médical */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-secondary mb-6">Récapitulatif du questionnaire médical</h2>

            {/* <div className="bg-primary text-white p-4 rounded-t-lg font-bold">
              Questionnaire Médical
            </div> */}
            <div className="border border-gray-300">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 p-3 text-left font-bold">Questions</th>
                      <th className="border border-gray-300 p-3 text-left font-bold">Assuré Principal</th>
                      {hasSpouse && <th className="border border-gray-300 p-3 text-left font-bold">Conjoint(e)</th>}
                      {[...Array(numberOfChildren)].map((_, i) => (
                        <th key={i} className="border border-gray-300 p-3 text-left font-bold">Enfant {i + 1}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {medicalQuestions.map((question, rowIndex) => (
                      <tr key={question.key} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-300 p-3 font-medium">{question.name}</td>
                        <td className="border border-gray-300 p-3 whitespace-pre-wrap">
                          {principalResponses
                            .filter(r => r.question === question.name)
                            .map(r => r.answer)
                            .join('\n') || 'Aucun'}
                        </td>
                        {hasSpouse && (
                          <td className="border border-gray-300 p-3 whitespace-pre-wrap">
                            {spouseResponses
                              .filter(r => r.question === question.name)
                              .map(r => r.answer)
                              .join('\n') || 'Aucun'}
                          </td>
                        )}
                        {[...Array(numberOfChildren)].map((_, i) => (
                          <td key={i} className="border border-gray-300 p-3 whitespace-pre-wrap">
                            {childrenResponses[i]?.responses
                              .filter(r => r.question === question.name)
                              .map(r => r.answer)
                              .join('\n') || 'Aucun'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Boutons de Navigation */}
          <div className="flex justify-center gap-4 mt-8">
            <Link
              to="/step-6"
              className="rounded-full border border-gray-300 px-6 py-3 text-gray-700 flex items-center gap-2 hover:bg-gray-50 transition"
            >
              ← Retour
            </Link>
            <button
              onClick={handleContinue}
              disabled={!declared}
              className={`rounded-full px-8 py-3 text-secondary font-medium flex items-center gap-2 transition ${
                declared
                  ? 'bg-primary hover:bg-primary-dark cursor-pointer'
                  : 'bg-gray-400 cursor-not-allowed opacity-50'
              }`}
            >
              Continuer →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
